import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTE_PATHS } from '../../app/router/route-paths';
import { fetchScheduleSlots } from '../../features/schedule-slots/api/schedule-slots.api';
import type { ScheduleSlotListRow } from '../../features/schedule-slots/api/schedule-slots.types';
import { isTrainerBranchRequiredApiError } from '../../features/trainer/lib/trainer-active-branch';
import { TrainerActiveBranchPicker } from '../../features/trainer/ui/TrainerActiveBranchPicker';
import { todayYmd } from '../../features/weekly-calendar/utils/timeUtils';
import { useOrgScheduleTimezone } from '../../features/weekly-calendar/hooks/useOrgScheduleTimezone';
import { isRouteAllowed } from '../../shared/access/access';
import { useAuth } from '../../shared/auth/use-auth';
import { userFacingLoadError } from '../../shared/lib/staff-user-facing-error';
import { Alert } from '../../shared/ui/Alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../shared/ui/Card';
import { Spinner } from '../../shared/ui/Spinner';

const SCHEDULE_ROUTE = '/schedule' as const;

function compareSlotTime(a: ScheduleSlotListRow, b: ScheduleSlotListRow): number {
  const ta = `${a.slot_date}T${a.start_time}`;
  const tb = `${b.slot_date}T${b.start_time}`;
  return ta.localeCompare(tb);
}

/** Карточка «сегодня в расписании» для тренера (вместо блока предупреждений). */
export default function StaffDashboardTrainerTodayScheduleCard() {
  const { apiClient, me, impersonateOrgId } = useAuth();
  const caps = me?.capabilities;
  const routeOk = isRouteAllowed(caps, SCHEDULE_ROUTE);
  const isTrainer = (me?.role ?? '').trim().toLowerCase() === 'trainer';

  const organizationId = useMemo(() => {
    const imp = impersonateOrgId != null && impersonateOrgId > 0 ? impersonateOrgId : null;
    const oid = me?.organization_id != null && me.organization_id > 0 ? me.organization_id : null;
    return imp ?? oid;
  }, [impersonateOrgId, me?.organization_id]);

  const branchId = me?.active_branch_id != null && me.active_branch_id > 0 ? me.active_branch_id : null;

  const scheduleTimezone = useOrgScheduleTimezone({ apiClient, organizationId, branchId });
  const today = todayYmd(scheduleTimezone);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [branchRequired, setBranchRequired] = useState(false);
  const [slots, setSlots] = useState<ScheduleSlotListRow[]>([]);

  const load = useCallback(async () => {
    if (!isTrainer || !routeOk || organizationId == null) {
      setSlots([]);
      setError(null);
      setBranchRequired(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setBranchRequired(false);
    try {
      const list = await fetchScheduleSlots(apiClient, {
        organization_id: organizationId,
        from_date: today,
        to_date: today,
        branch_id: branchId
      });
      const sorted = [...list].sort(compareSlotTime);
      setSlots(sorted.slice(0, 5));
    } catch (e) {
      setSlots([]);
      if (isTrainerBranchRequiredApiError(e)) {
        setBranchRequired(true);
        setError(null);
      } else {
        setError(userFacingLoadError(e));
      }
    } finally {
      setLoading(false);
    }
  }, [apiClient, branchId, isTrainer, organizationId, routeOk, today]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!isTrainer || !routeOk) return null;

  return (
    <Card data-testid="trainer-dashboard-today-schedule">
      <CardHeader>
        <CardTitle>Сегодня в расписании</CardTitle>
        <CardDescription>Ближайшие слоты на сегодня в выбранном филиале.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {branchRequired ? (
          <div className="space-y-2">
            <Alert variant="warning" title="Выберите филиал для работы">
              Чтобы загрузить слоты, укажите активный филиал — для тренера с несколькими площадками это обязательно.
            </Alert>
            <TrainerActiveBranchPicker onApplied={() => void load()} />
          </div>
        ) : null}
        {loading ? (
          <Spinner label="Загрузка расписания…" />
        ) : null}
        {!loading && error ? (
          <Alert variant="error" title="Не удалось загрузить расписание">
            {error}
          </Alert>
        ) : null}
        {!loading && !error && !branchRequired && slots.length === 0 ? (
          <p className="staff-m-0 text-sm text-[hsl(var(--ui-muted-foreground))]" data-testid="trainer-today-schedule-empty">
            На сегодня слотов нет.
          </p>
        ) : null}
        {!loading && !error && slots.length > 0 ? (
          <ul className="staff-m-0 list-none space-y-2 p-0" data-testid="trainer-today-schedule-list">
            {slots.map((s) => (
              <li
                key={s.id}
                className="rounded-sm border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-muted)/0.2)] px-3 py-2 text-sm"
              >
                <div className="font-medium text-[hsl(var(--ui-foreground))]">
                  {s.start_time}–{s.end_time}
                </div>
                <div className="text-[hsl(var(--ui-muted-foreground))]">
                  <span className={s.status === 'cancelled' ? 'line-through' : undefined}>{s.service_name}</span>
                  {s.status === 'cancelled' ? (
                    <span className="ml-2 font-medium text-[hsl(var(--ui-destructive))]">Отменено</span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : null}
        <Link
          to={ROUTE_PATHS.schedule}
          className="inline-block text-sm font-medium text-[hsl(var(--ui-primary))] underline-offset-2 hover:underline"
          data-testid="trainer-today-schedule-open-link"
        >
          Открыть расписание
        </Link>
      </CardContent>
    </Card>
  );
}
